/**
 * Document Language Helper
 * Allows clinicians to work in their preferred language
 * and convert documentation back to English
 */

import React, { useState } from 'react';
import { Globe, Languages, ArrowLeftRight, Check, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from '../context/TranslationContext';
import api from '../api';

const DocumentLanguageHelper = ({ onLanguageChange, showConvertButton = true }) => {
  const { currentLanguage, changeLanguage, translateText, t, isTranslating } = useTranslation();
  const [originalLanguage, setOriginalLanguage] = useState(null);
  const [isConverted, setIsConverted] = useState(false);
  const [isConvertingText, setIsConvertingText] = useState(false);

  const isEnglish = currentLanguage === 'en';

  // Convert to English for submission
  const handleConvertToEnglish = async () => {
    if (!isEnglish) {
      setIsConvertingText(true);
      setOriginalLanguage(currentLanguage);
      await changeLanguage('en');
      setIsConverted(true);
      setIsConvertingText(false);
      if (onLanguageChange) {
        onLanguageChange('en', currentLanguage);
      }
    }
  };

  // Revert back to original language
  const handleRevertToOriginal = async () => {
    if (originalLanguage) {
      setIsConvertingText(true);
      await changeLanguage(originalLanguage);
      setIsConverted(false);
      setIsConvertingText(false);
      if (onLanguageChange) {
        onLanguageChange(originalLanguage, 'en');
      }
    }
  };

  // Get language display name
  const getLanguageName = (code) => {
    const names = {
      en: 'English', es: 'Spanish', fr: 'French', zh: 'Chinese',
      ru: 'Russian', uk: 'Ukrainian', vi: 'Vietnamese', tl: 'Filipino',
      hi: 'Hindi', ur: 'Urdu', ja: 'Japanese', ko: 'Korean', th: 'Thai',
      fa: 'Farsi', ar: 'Arabic', he: 'Hebrew', ga: 'Irish', de: 'German',
      pt: 'Portuguese', it: 'Italian', pl: 'Polish', nl: 'Dutch',
      af: 'Afrikaans', sw: 'Swahili', am: 'Amharic', om: 'Oromo',
      ti: 'Tigrinya', sid: 'Sidamo', wal: 'Wolaytta', gur: 'Gurage',
      had: 'Hadiyya', gam: 'Gamo', aar: 'Afar', ak: 'Twi',
      fat: 'Fanti', dag: 'Dagomba', gua: 'Guan', ha: 'Hausa',
      ig: 'Igbo', ee: 'Ewe', yo: 'Yoruba', zu: 'Zulu', xh: 'Xhosa', so: 'Somali'
    };
    return names[code] || code;
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-blue-600" />
          <span className="text-sm font-medium text-blue-800">
            {t('language.currentLanguage', 'Documentation Language')}:
          </span>
          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm font-semibold flex items-center gap-1">
            {isTranslating && <Loader2 className="w-3 h-3 animate-spin" />}
            {getLanguageName(currentLanguage)}
          </span>
        </div>

        {showConvertButton && (
          <div className="flex items-center gap-2">
            {!isEnglish && !isConverted && (
              <Button
                size="sm"
                onClick={handleConvertToEnglish}
                disabled={isConvertingText || isTranslating}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="convert-to-english-btn"
              >
                {isConvertingText ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <ArrowLeftRight className="w-4 h-4 mr-1" />
                )}
                {t('language.convertToEnglish', 'Convert to English')}
              </Button>
            )}

            {isConverted && originalLanguage && (
              <>
                <span className="text-sm text-green-600 flex items-center gap-1">
                  <Check className="w-4 h-4" />
                  {t('common.converted', 'Converted from')} {getLanguageName(originalLanguage)}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleRevertToOriginal}
                  disabled={isConvertingText || isTranslating}
                  className="text-blue-600 border-blue-300"
                >
                  {isConvertingText ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Languages className="w-4 h-4 mr-1" />
                  )}
                  {t('common.backTo', 'Back to')} {getLanguageName(originalLanguage)}
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {!isEnglish && !isConverted && (
        <p className="text-xs text-blue-600 mt-2">
          You can document in {getLanguageName(currentLanguage)} and convert to English before submitting.
        </p>
      )}
    </div>
  );
};

export default DocumentLanguageHelper;
